import { pick, serverMap } from '@/shared/lib/server-map'
import type { BookingDetail } from '@/shared/api'

// Типы выведены из контракта индексным доступом: схема лежит в `BookingDetail` инлайном, отдельного
// имени у неё нет, и рукописная копия разъехалась бы с ней молча.
export type BookingPayments = BookingDetail['payments']
export type BookingPaymentItem = BookingPayments['items'][number]
export type PaymentState = BookingPaymentItem['state']

// Незнакомое состояние — серый: `--st-in` заявил бы «деньги у нас», `--st-pending` — «ждём платёж».
// Правило целиком — в `model/server-drift.ts`.
const FALLBACK_TOKEN = 'var(--ink-3)'

/**
 * Состояние позиции → токен цвета пилюли и точки. Значения — из `docs/reference/phase-2-mockup.md` §3.
 *
 * ⚠️ `returned` В САМОМ МАКЕТЕ НЕ НАРИСОВАН НИ РАЗУ — образец там искать бесполезно; значение взято
 * из справочника (§3 перечисляет все четыре), а не выведено по соседям.
 *
 * ⚠️ `satisfies` стоит на ЛИТЕРАЛЕ, до `serverMap`: так новое состояние контракта роняет СБОРКУ
 * здесь, а не уезжает молча в серый фолбэк. Проверено машиной в обе стороны: полная карта
 * компилируется, карта без `returned` падает с `TS2741`; на живом случае защита уже срабатывала —
 * `voided` пришёл пятым и тихо уехать в фолбэк не смог.
 */
const STATE_TOKENS = serverMap({
  paid: 'var(--st-in)',
  pending: 'var(--st-pending)',
  held: 'var(--st-in)',
  returned: 'var(--st-in)',
  // Отмена — не событие с исходом: записи просто больше нет в своде. Зелёный сказал бы «хорошо
  // кончилось», красный — «беда»; нейтральный не утверждает ничего.
  voided: 'var(--ink-3)',
} satisfies Record<PaymentState, string>)

export function paymentStateToken(state: PaymentState): string {
  return pick(STATE_TOKENS, state, FALLBACK_TOKEN)
}

/**
 * Ширина заливки прогресс-бара, в процентах.
 *
 * ⚠️ ЗАЖИМ НЕСУЩИЙ: `paidPct` в контракте — голый `integer` без границ, а `width: '-5%'` невалидна,
 * браузер объявление ОТБРАСЫВАЕТ, и блочный `div` заливки берёт `width: auto` — полоса показывает
 * «оплачено полностью» ровно там, где не оплачено ничего, и молча.
 *
 * `Number.isFinite` — заслон от того, что тип параметра отрицает, а кривой шлюз присылает: строки,
 * `null`, `undefined`, `Infinity` (JSON бесконечности не переносит, но арифметика на строке — да).
 * Ловит их всех он сам, до единой операции. Не-число — это «не знаем, сколько оплачено», и честный
 * ответ `0`: пустая полоса ничего не обещает, полная — обещает всё.
 */
export function paidBarPct(pct: number): number {
  if (!Number.isFinite(pct)) return 0
  return Math.min(100, Math.max(0, pct))
}
